/**
 * GPPA Nonce Refresh
 *
 * Fixes stale nonce issues when pages are edge-cached (Cloudflare/BigScoots).
 * Intercepts GPPA Advanced Select AJAX requests and refreshes the nonce
 * before the first request fires.
 *
 * @package
 */

const GPPANonceRefresh = (() => {
	'use strict';

	let nonceRefreshed = false;
	let nonceRefreshing = false;
	const pendingCallbacks = [];

	/**
	 * Get the AJAX URL, with fallback to WordPress default.
	 *
	 * @return {string} The AJAX URL.
	 */
	const getAjaxUrl = () => {
		return window.GPPA?.AJAXURL || window.ajaxurl || '/wp-admin/admin-ajax.php';
	};

	/**
	 * Fetch a fresh nonce from the server.
	 *
	 * @return {Promise<string>} Resolves with the fresh nonce.
	 */
	const refreshNonce = async () => {
		if (nonceRefreshed) {
			return window.GPPA?.NONCE;
		}

		if (nonceRefreshing) {
			return new Promise((resolve) => {
				pendingCallbacks.push(resolve);
			});
		}

		nonceRefreshing = true;

		try {
			const response = await fetch(`${getAjaxUrl()}?action=get_fresh_gppa_nonce`, {
				method: 'POST',
				credentials: 'same-origin',
				headers: {
					'Content-Type': 'application/x-www-form-urlencoded',
				},
			});

			if (!response.ok) {
				throw new Error(`Nonce refresh failed: ${response.status}`);
			}

			const data = await response.json();

			if (!data.success || !data.data?.nonce) {
				throw new Error('Invalid nonce response');
			}

			// Update GPPA.NONCE if GPPA exists.
			if (window.GPPA) {
				window.GPPA.NONCE = data.data.nonce;
			}

			nonceRefreshed = true;

			// Resolve any pending callbacks.
			const freshNonce = data.data.nonce;
			pendingCallbacks.forEach((callback) => callback(freshNonce));
			pendingCallbacks.length = 0;

			return freshNonce;
		} catch (error) {
			console.warn('GPPA nonce refresh failed:', error); // eslint-disable-line no-console
			throw error;
		} finally {
			nonceRefreshing = false;
		}
	};

	/**
	 * Check if a URL is a GPPA Advanced Select request.
	 *
	 * @param {string} url - The URL to check.
	 * @return {boolean} Whether the URL is a GPPA Advanced Select request.
	 */
	const isGPPARequest = (url) => url?.includes('gp_advanced_select_get_gppa_results') ?? false;

	/**
	 * Update the security field in request data with the fresh nonce.
	 *
	 * @param {string} data       - JSON string of request data.
	 * @param {string} freshNonce - The fresh nonce to use.
	 * @return {string} Updated JSON string.
	 */
	const updateRequestData = (data, freshNonce) => {
		if (typeof data !== 'string') {
			return data;
		}

		try {
			const parsed = JSON.parse(data);
			parsed.security = freshNonce;
			return JSON.stringify(parsed);
		} catch {
			return data;
		}
	};

	/**
	 * Initialize the jQuery AJAX interceptor.
	 */
	const initInterceptor = () => {
		if (typeof window.jQuery === 'undefined') {
			console.warn('GPPANonceRefresh: jQuery not found'); // eslint-disable-line no-console
			return;
		}

		const $ = window.jQuery;
		const originalAjax = $.ajax;

		$.ajax = function (url, options) {
			// Handle jQuery.ajax(settings) signature.
			if (typeof url === 'object') {
				options = url;
				url = options.url;
			}

			options = options || {};
			const requestUrl = url || options.url;

			// Check if this is a GPPA Advanced Select request.
			if (!isGPPARequest(requestUrl) || nonceRefreshed) {
				return originalAjax.call($, url, options);
			}

			// Return a deferred that waits for nonce refresh.
			const deferred = $.Deferred();

			refreshNonce()
				.then((freshNonce) => {
					options.data = updateRequestData(options.data, freshNonce);
					return originalAjax.call($, url, options);
				})
				.then(
					(...args) => deferred.resolve(...args),
					(...args) => deferred.reject(...args)
				)
				.catch(() => {
					// Proceed with original nonce if refresh failed.
					originalAjax.call($, url, options).then(
						(...args) => deferred.resolve(...args),
						(...args) => deferred.reject(...args)
					);
				});

			return deferred.promise();
		};
	};

	// Initialize immediately - jQuery should be available since we depend on it.
	initInterceptor();

	// Expose for debugging.
	return {
		refreshNonce,
		isRefreshed: () => nonceRefreshed,
	};
})();

// Export for module systems if available.
if (typeof module !== 'undefined' && module.exports) {
	module.exports = GPPANonceRefresh;
}
